import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { getMediaUrl } from '@onesource/services';
import { FC } from 'react';
import { MdLocationOn } from 'react-icons/md';

export type ProDirectoryPreviewData = {
  fullName?: string | null;
  title?: string | null;
  casualImage?: string | null;
  profession?: Array<{ id: number | string; name: string }>;
  expertise?: string[];
  businessCity?: string | null;
  businessState?: string | null;
  businessCountry?: string | null;
};

type Props = {
  data: ProDirectoryPreviewData;
};

const MAX_EXPERTISE_CHIPS = 3;

export const ProDirectoryPreviewCard: FC<Props> = ({ data }) => {
  const theme = useTheme();

  const displayName = data.fullName || 'Your Name';
  const location = [data.businessCity, data.businessState, data.businessCountry].filter(Boolean).join(', ');
  const professionText = (data.profession ?? []).map(p => p.name).join(', ');
  const visibleExpertise = (data.expertise ?? []).slice(0, MAX_EXPERTISE_CHIPS);
  const remainingExpertise = (data.expertise ?? []).length - MAX_EXPERTISE_CHIPS;

  return (
    <Box
      sx={{
        width: 285,
        height: 255,
        borderRadius: 3,
        bgcolor: 'background.paper',
        boxShadow: '0 1px 8px rgba(0,0,0,0.10)',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Avatar (left) + name/title (right) */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, pt: 2, pb: 1.5 }}>
        <Avatar
          src={data.casualImage ? getMediaUrl(data.casualImage) : undefined}
          alt={displayName}
          sx={{ width: 52, height: 52, flexShrink: 0, fontSize: '1.25rem', fontWeight: 700 }}
        >
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-word', lineHeight: 1.3 }}>
            {displayName}
          </Typography>
          {data.title && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {data.title}
            </Typography>
          )}
        </Box>
      </Stack>

      {/* Body */}
      <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Stack direction="row" spacing={0.5} alignItems="flex-start">
          <MdLocationOn size={14} color={theme.palette.text.secondary} style={{ marginTop: 2, flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary">
            {location || 'No location added'}
          </Typography>
        </Stack>

        {professionText && (
          <Typography
            variant="caption"
            color="text.primary"
            sx={{
              display: '-webkit-box',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {professionText}
          </Typography>
        )}

        {(data.expertise ?? []).length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.25 }}>
            {visibleExpertise.map(tag => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{
                  bgcolor: '#f6f2f1',
                  color: 'primary.light',
                  border: 'none',
                }}
              />
            ))}
            {remainingExpertise > 0 && (
              <Chip
                label={`+${remainingExpertise}`}
                size="small"
                sx={{ bgcolor: '#f6f2f1', color: 'primary.light', border: 'none' }}
              />
            )}
          </Stack>
        )}
      </Box>

      <Box flex={1} />

      {/* View profile button */}
      <Box sx={{ px: 2, pb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{
            fontWeight: 700,
            py: 1,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          View profile
        </Button>
      </Box>
    </Box>
  );
};
